import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  selectCurrentOrFirstContract,
  selectCurrentOrFirstContractId,
  selectIdentityContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'

export const IDENTITY_DOCUMENT_NUMBER_KEY = 'identity_document_number'
export const IDENTITY_EXPIRATION_DATE_KEY = 'identity_expiration_date'
export const IDENTITY_CREATED_DATE_KEY = 'identity_created_date'
export const IDENTITY_ISSUING_ENTITY_KEY = 'identity_issuing_entity'
export const IDENTITY_ISSUING_CITY_KEY = 'identity_issuing_city'

const useIdentityValidation = () => {
  const { t } = useTranslation()
  const { addErrorToContract, removeContractError } = useFormStatus()

  // selectCurrentOrFirstContractId returns the active contract tab ID when on a
  // contract tab, falling back to the first opened contract when on the Contact tab.
  // This prevents the effect from clearing errors simply because the Contact tab is active.
  const contractId = useSelector(selectCurrentOrFirstContractId)
  const contract = useSelector(selectCurrentOrFirstContract)

  const identityDocumentMandatoryOperations: string[] =
    contract?.config?.providerConfiguration?.identityDocumentMandatoryOperations ?? []
  const prestationType: string = contract?.characteristics?.prestationType ?? ''
  const isMandatory = identityDocumentMandatoryOperations.includes(prestationType)

  const { documentNumber, expirationDate, createdDate, issuingEntity, issuingCity } =
    useSelector(selectIdentityContactSubscriptionData) ?? {}

  useEffect(() => {
    if (!contractId) return

    const fields: Array<{ key: string; value: unknown; msgKey: string }> = [
      { key: IDENTITY_DOCUMENT_NUMBER_KEY, value: documentNumber, msgKey: 'missing_document_number' },
      { key: IDENTITY_EXPIRATION_DATE_KEY, value: expirationDate, msgKey: 'missing_expiration_date' },
      { key: IDENTITY_CREATED_DATE_KEY, value: createdDate, msgKey: 'missing_created_date' },
      { key: IDENTITY_ISSUING_ENTITY_KEY, value: issuingEntity, msgKey: 'missing_issuing_entity' },
      { key: IDENTITY_ISSUING_CITY_KEY, value: issuingCity, msgKey: 'missing_issuing_city' },
    ]

    fields.forEach(({ key, value, msgKey }) => {
      if (isMandatory && !value) {
        addErrorToContract(contractId, { [key]: t(`subscriptionForm:identity_module.${msgKey}`) })
      } else {
        removeContractError(contractId, key)
      }
    })
  }, [isMandatory, documentNumber, expirationDate, createdDate, issuingEntity, issuingCity, contractId])
}

export default useIdentityValidation
